import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useOnboarding, ModuleTips } from '../components/onboarding';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Download, FileText, DollarSign, TrendingUp, Building,
  ChevronDown, ChevronRight, Info, Package, Copy, Mail
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Reports = () => {
  const { t } = useTranslation();
  const { user, getAuthHeaders } = useAuth();
  const { triggerAction } = useOnboarding();
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [scheduleC, setScheduleC] = useState(null);
  const [yearComparison, setYearComparison] = useState(null);
  const [topDeductions, setTopDeductions] = useState([]);
  const [expandedLines, setExpandedLines] = useState({});
  const [generatingPackage, setGeneratingPackage] = useState(false);

  // 12 years: next year + current + 10 years back (e.g. 2027→2016 in 2026)
  const years = Array.from({ length: 12 }, (_, i) => new Date().getFullYear() + 1 - i);

  useEffect(() => {
    fetchReports();
    // Trigger onboarding action when user views reports page
    triggerAction('generate_report');
  }, [selectedYear]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const [scheduleRes, comparisonRes, topRes] = await Promise.all([
        axios.get(`${API}/reports/schedule-c?year=${selectedYear}`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/analytics/year-comparison?years=${selectedYear - 1},${selectedYear}`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/analytics/top-deductions?year=${selectedYear}`, { headers: getAuthHeaders(), withCredentials: true })
      ]);
      
      setScheduleC(scheduleRes.data);
      setYearComparison(comparisonRes.data);
      setTopDeductions(topRes.data);
    } catch (error) {
      toast.error(t('toast.failed_to_load_reports'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPackage = async () => {
    setGeneratingPackage(true);
    try {
      const response = await axios.get(`${API}/reports/tax-package?year=${selectedYear}`, {
        headers: getAuthHeaders(),
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tax_package_${selectedYear}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t('reports.package_downloaded'));
      // Trigger onboarding action when user creates tax package
      triggerAction('create_tax_package');
    } catch (error) {
      toast.error(t('reports.failed_to_generate'));
    } finally {
      setGeneratingPackage(false);
    }
  };

  const handleExportDeductionsCSV = async () => {
    try {
      const response = await axios.get(`${API}/deductions/export/csv?year=${selectedYear}`, {
        headers: getAuthHeaders(),
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `deductions_${selectedYear}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t('reports.deductions_exported'));
    } catch (error) {
      toast.error(t('reports.failed_to_export'));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const toggleLine = (lineKey) => {
    setExpandedLines(prev => ({
      ...prev,
      [lineKey]: !prev[lineKey]
    }));
  };

  const copyEmailDraft = async () => {
    const draft = `Subject: Tax Documents for ${selectedYear} - Ready for Review

Dear [Accountant Name],

I have prepared my tax documents for the ${selectedYear} tax year and would appreciate your review.

Financial Summary:
- Gross Income: ${formatCurrency(scheduleC?.gross_income)}
- Total Expenses: ${formatCurrency(scheduleC?.total_expenses)}
- Net Income: ${formatCurrency(scheduleC?.net_profit)}

Please let me know if you need any additional information.

Best regards,
${user?.name}`;

    try {
      await navigator.clipboard.writeText(draft);
      toast.success(t('reports.email_copied'));
    } catch (error) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = draft;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success(t('reports.email_copied'));
      } catch (fallbackError) {
        toast.error(t('reports.copy_failed', 'Failed to copy. Please copy manually.'));
      }
      document.body.removeChild(textArea);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
  <>
    <DashboardLayout>
      <div className="space-y-6" data-testid="reports-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-1">{t('reports.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('reports.subtitle')}</p>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-32" data-testid="year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
              </SelectContent>
            </Select>

            <Button variant="outline" onClick={handleExportDeductionsCSV} data-testid="export-deductions-btn">
              <Download className="w-4 h-4 mr-2" />
              {t('reports.export_csv')}
            </Button>

            <Button onClick={handleDownloadPackage} disabled={generatingPackage} data-testid="download-package-btn">
              <Package className="w-4 h-4 mr-2" />
              {generatingPackage ? t('reports.generating') : t('reports.tax_package')}
            </Button>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="shadow-soft border-border/40" data-testid="email-draft-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">{t('reports.accountant_email_draft')}</h3>
                  <p className="text-sm text-muted-foreground">{t('reports.email_draft_desc')}</p>
                </div>
                <Button variant="outline" onClick={copyEmailDraft} data-testid="copy-email-btn">
                  <Copy className="w-4 h-4 mr-2" />
                  {t('reports.copy')}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-soft border-border/40" data-testid="package-info-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Package className="w-6 h-6 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">{t('reports.one_click_package')}</h3>
                  <p className="text-sm text-muted-foreground">{t('reports.package_desc')}</p>
                </div>
                <Button onClick={handleDownloadPackage} disabled={generatingPackage}>
                  <Download className="w-4 h-4 mr-2" />
                  {t('reports.download')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Year Comparison */}
        {yearComparison && Object.keys(yearComparison).length > 0 && (
          <Card className="shadow-soft border-border/40" data-testid="year-comparison-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                {t('reports.year_comparison')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(yearComparison || {}).map(([year, data]) => (
                  <div key={year} className="p-4 bg-muted/50 rounded-lg">
                    <h4 className="font-semibold text-lg mb-3">{year}</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('reports.income')}</span>
                        <span className="mono text-emerald-600">{formatCurrency(data.income)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('reports.expenses')}</span>
                        <span className="mono text-red-600">{formatCurrency(data.expenses)}</span>
                      </div>
                      <div className="flex justify-between border-t pt-2 mt-2">
                        <span className="font-medium">{t('reports.net_income')}</span>
                        <span className="mono font-bold">{formatCurrency(data.net_income)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('reports.estimated_tax')}</span>
                        <span className="mono">{formatCurrency(data.estimated_tax)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Schedule C Preview */}
        {scheduleC && (
          <Card className="shadow-soft border-border/40" data-testid="schedule-c-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {t('reports.schedule_c_preview')} - {selectedYear}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {scheduleC.business && (
                <div className="mb-6 p-4 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Building className="w-5 h-5 text-primary" />
                    <div>
                      <p className="font-medium">{scheduleC.business.name}</p>
                      <p className="text-sm text-muted-foreground capitalize">
                        {scheduleC.business.entity_type?.replace('_', ' ')}
                        {scheduleC.business.ein && ` • EIN: ${scheduleC.business.ein}`}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                {/* Part I - Income */}
                <div className="p-4 bg-emerald-50 rounded-lg">
                  <h4 className="font-semibold text-emerald-800 mb-2">{t('reports.part_i_income')}</h4>
                  <div className="flex justify-between">
                    <span>{t('reports.gross_receipts')}</span>
                    <span className="mono font-bold">{formatCurrency(scheduleC.gross_income)}</span>
                  </div>
                </div>

                {/* Part II - Expenses */}
                <div className="p-4 bg-red-50 rounded-lg">
                  <h4 className="font-semibold text-red-800 mb-3">{t('reports.part_ii_expenses')}</h4>
                  <div className="space-y-2">
                    {Object.entries(scheduleC.line_items || {})
                      .filter(([key]) => !['line_1', 'line_7', 'line_28', 'line_31'].includes(key))
                      .sort((a, b) => b[1].amount - a[1].amount)
                      .map(([key, item]) => (
                        <div key={key} className="flex justify-between py-1 border-b border-red-100 last:border-0">
                          <span className="text-sm">{item.name}</span>
                          <span className="mono">{formatCurrency(item.amount)}</span>
                        </div>
                      ))}
                    <div className="flex justify-between pt-2 border-t border-red-200 font-semibold">
                      <span>{t('reports.total_expenses')}</span>
                      <span className="mono">{formatCurrency(scheduleC.total_expenses)}</span>
                    </div>
                  </div>
                </div>

                {/* Net Profit */}
                <div className={`p-4 rounded-lg ${scheduleC.net_profit >= 0 ? 'bg-blue-50' : 'bg-orange-50'}`}>
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-semibold">{t('reports.net_profit_loss')}</h4>
                      <p className="text-sm text-muted-foreground">{t('reports.goes_to_form_1040')}</p>
                    </div>
                    <span className={`text-2xl mono font-bold ${scheduleC.net_profit >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                      {formatCurrency(scheduleC.net_profit)}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Top Deductions */}
        {topDeductions.length > 0 && (
          <Card className="shadow-soft border-border/40" data-testid="top-deductions-card">
            <CardHeader>
              <CardTitle>{t('reports.top_deduction_categories')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {topDeductions.map((item, index) => {
                  // ── Commandment II: use backend-computed percentage ──
                  const percentage = item.percentage ?? 0;
                  
                  return (
                    <div key={item.category} className="flex items-center gap-4">
                      <span className="w-6 text-sm text-muted-foreground">{index + 1}.</span>
                      <div className="flex-1">
                        <div className="flex justify-between mb-1">
                          <span className="font-medium capitalize">{item.category.replace('_', ' ')}</span>
                          <span className="mono">{formatCurrency(item.amount)}</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary rounded-full"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-sm text-muted-foreground w-16 text-right">
                        {item.count} {t('reports.items')}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Disclaimer */}
        <Card className="shadow-soft border-border/40 bg-amber-50 border-amber-200" data-testid="disclaimer-card">
          <CardContent className="p-4 flex gap-3">
            <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-amber-800">
                <strong>{t('reports.disclaimer')}:</strong> {t('reports.disclaimer_text')}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
    <ModuleTips moduleId="reports" />
  </>
  );
};

export default Reports;
